import { useEffect, useRef, useState, useCallback } from "react";
import { Volume2, VolumeX } from "lucide-react";
import { gameSounds } from "../../lib/gameSounds";

/**
 * 2D vertical (portrait) 3-lane jetski runner.
 * - Player is the user-provided golden retriever jetski rider PNG (transparent).
 * - Obstacles scroll top → bottom; ArrowLeft/Right or swipe to change lanes.
 *
 * Props:
 *   onGameOver(coins, durationMs) — called once when the run ends
 *   paused                        — pauses simulation
 *   seed                          — optional integer; when set, obstacle/coin
 *                                   spawns are deterministic (used by Arena PvP)
 *   durationSec                   — optional fixed game duration in seconds
 *                                   (auto-ends the run; used by Arena PvP)
 */

// Mulberry32 — small deterministic PRNG. Returns floats in [0, 1).
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) | 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export const GameCanvas = ({ onGameOver, onProgress = null, paused = false, seed = null, durationSec = 0 }) => {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const [hudCoins, setHudCoins] = useState(0);
  const [hudDistance, setHudDistance] = useState(0);
  const [hudTime, setHudTime] = useState(durationSec || 0);
  const [muted, setMuted] = useState(() => localStorage.getItem("jd-muted") === "1");
  const toggleMute = () => setMuted((m) => { gameSounds.setMuted(!m); return !m; });

  const riderRef = useRef(null);
  const spritesRef = useRef({});
  useEffect(() => {
    const img = new Image();
    img.src = "/game/jetdog-rider-back.png";
    img.onload = () => { riderRef.current = img; };
    const files = { buoy: "sprite-buoy.png", rock: "sprite-rock.png", wave: "sprite-wave.png", shark: "sprite-shark.png", barrel: "sprite-barrel.png", ramp: "sprite-ramp.png", coin: "sprite-coin.png", bg: "bg-horizon.jpg" };
    Object.entries(files).forEach(([key, f]) => {
      const sp = new Image();
      sp.src = `/game/${f}`;
      sp.onload = () => { spritesRef.current[key] = sp; };
    });
  }, []);

  const init = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const W = canvas.clientWidth;
    const H = canvas.clientHeight;
    canvas.width = W * dpr;
    canvas.height = H * dpr;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const lanesX = [W * 0.22, W * 0.5, W * 0.78];
    const rng = seed != null ? mulberry32(Number(seed)) : Math.random;
    stateRef.current = {
      W, H, ctx,
      rng,
      durationMs: durationSec > 0 ? durationSec * 1000 : 0,
      laneCount: 3,
      laneX: lanesX,
      playerLane: 1,
      playerX: lanesX[1],
      playerY: H * 0.8,
      targetX: lanesX[1],
      obstacles: [],
      coins: [],
      bursts: [],
      foam: Array.from({ length: 70 }, () => ({
        x: Math.random() * W,
        y: H * 0.25 + Math.random() * (H * 0.72),
        r: 1.5 + Math.random() * 3,
        a: 0.3 + Math.random() * 0.7,
      })),
      // Ambient visual elements (decorative only, do not affect gameplay)
      clouds: Array.from({ length: 4 }, (_, i) => ({
        x: (i / 4) * W + Math.random() * 60,
        y: H * (0.02 + Math.random() * 0.09),
        scale: 0.5 + Math.random() * 0.6,
        speed: 6 + Math.random() * 10,
      })),
      glints: Array.from({ length: 26 }, () => ({
        xf: (Math.random() * 2 - 1) * 0.95,
        o: Math.random(),
        tw: Math.random() * Math.PI * 2,
      })),
      birds: Array.from({ length: 2 }, (_, i) => ({
        x: (i / 2) * W + Math.random() * W * 0.3,
        y: H * (0.03 + Math.random() * 0.07),
        phase: Math.random() * Math.PI * 2,
        speed: 25 + Math.random() * 15,
      })),
      islands: [
        { x: W * 0.2, w: 110, h: 16 },
        { x: W * 0.62, w: 150, h: 20 },
        { x: W * 0.92, w: 90, h: 13 },
      ],
      droplets: [],
      powerups: [],
      powerupTimer: 7 + rng() * 6,
      shieldT: 0,
      magnetT: 0,
      ramps: [],
      rampTimer: 6 + rng() * 5,
      airT: 0,
      flotsam: Array.from({ length: 7 }, () => ({
        xf: (0.55 + Math.random() * 0.38) * (Math.random() < 0.5 ? -1 : 1),
        o: Math.random(),
        r: 6 + Math.random() * 10,
      })),
      bands: Array.from({ length: 9 }, (_, i) => ({ o: i / 9 })),
      streaks: Array.from({ length: 12 }, () => ({
        xf: (0.08 + Math.random() * 0.47) * (Math.random() < 0.5 ? -1 : 1),
        o: Math.random(),
        sp: 0.8 + Math.random() * 0.5,
      })),
      shake: 0,
      speed: 340,
      spawnTimer: 0,
      coinTimer: 0,
      coinCount: 0,
      distance: 0,
      startedAt: performance.now(),
      lastFrame: performance.now(),
      gameOver: false,
      tilt: 0,
      bobPhase: 0,
    };
    setHudCoins(0);
    setHudDistance(0);
    setHudTime(durationSec || 0);
  }, [seed, durationSec]);

  const moveLane = useCallback((delta) => {
    const s = stateRef.current;
    if (!s || s.gameOver) return;
    const next = Math.max(0, Math.min(s.laneCount - 1, s.playerLane + delta));
    if (next !== s.playerLane) {
      s.playerLane = next;
      s.targetX = s.laneX[next];
      s.tilt = delta * 0.28;
    }
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (paused) return;
      if (e.key === "ArrowLeft" || e.key === "a" || e.key === "A") moveLane(-1);
      else if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") moveLane(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [moveLane, paused]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    let startY = null;
    let startX = null;
    let startTime = 0;
    const onStart = (e) => {
      const t = e.touches ? e.touches[0] : e;
      startY = t.clientY;
      startX = t.clientX;
      startTime = performance.now();
    };
    const onEnd = (e) => {
      if (startX == null) return;
      const t = e.changedTouches ? e.changedTouches[0] : e;
      const endY = t.clientY;
      const endX = t.clientX;
      const dy = endY - startY;
      const dx = endX - startX;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const dur = performance.now() - startTime;
      // Swipe (clear horizontal drag)
      if (Math.abs(dx) > 28 && Math.abs(dx) > Math.abs(dy)) {
        moveLane(dx > 0 ? 1 : -1);
      } else if (dist < 14 && dur < 400) {
        // Tap-to-lane: jump directly to the lane corresponding to tap X
        const s = stateRef.current;
        if (s) {
          const rect = canvas.getBoundingClientRect();
          const localX = endX - rect.left;
          // Pick closest lane
          let bestIdx = 0, bestDelta = Infinity;
          for (let i = 0; i < s.laneX.length; i++) {
            const d = Math.abs(s.laneX[i] - localX);
            if (d < bestDelta) { bestDelta = d; bestIdx = i; }
          }
          const delta = bestIdx - s.playerLane;
          if (delta !== 0) moveLane(delta);
        }
      }
      startY = null;
      startX = null;
    };
    canvas.addEventListener("touchstart", onStart, { passive: true });
    canvas.addEventListener("touchend", onEnd);
    return () => {
      canvas.removeEventListener("touchstart", onStart);
      canvas.removeEventListener("touchend", onEnd);
    };
  }, [moveLane]);

  useEffect(() => {
    const onResize = () => init();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [init]);

  // Keep the latest onGameOver in a ref so the game loop doesn't restart
  // when the parent re-renders with a new function reference.
  const onGameOverRef = useRef(onGameOver);
  useEffect(() => { onGameOverRef.current = onGameOver; }, [onGameOver]);
  const onProgressRef = useRef(onProgress);
  useEffect(() => { onProgressRef.current = onProgress; }, [onProgress]);

  useEffect(() => {
    init();
    let raf;
    const loop = (t) => {
      const s = stateRef.current;
      if (!s) { raf = requestAnimationFrame(loop); return; }
      if (paused) { s.lastFrame = t; gameSounds.engineStop(); gameSounds.stopMusic(); draw(s, riderRef.current, spritesRef.current); raf = requestAnimationFrame(loop); return; }
      const dt = Math.min(0.05, (t - s.lastFrame) / 1000);
      s.lastFrame = t;
      if (!s.gameOver) { update(s, dt); gameSounds.engine(s.speed); gameSounds.startMusic(); } else { gameSounds.engineStop(); gameSounds.stopMusic(); }
      // Arena mode: auto-end on duration regardless of collision
      if (!s.gameOver && s.durationMs > 0) {
        const elapsed = performance.now() - s.startedAt;
        if (elapsed >= s.durationMs) {
          s.gameOver = true;
          s.timedOut = true;
        }
      }
      draw(s, riderRef.current, spritesRef.current);
      if (Math.floor(t / 100) % 2 === 0) {
        setHudCoins(s.coinCount);
        setHudDistance(Math.floor(s.distance));
        if (!s.gameOver && onProgressRef.current) onProgressRef.current(s.coinCount);
        if (s.durationMs > 0) {
          const remaining = Math.max(0, Math.ceil((s.durationMs - (performance.now() - s.startedAt)) / 1000));
          setHudTime(remaining);
        }
      }
      if (s.gameOver && !s._reported) {
        s._reported = true;
        const duration = performance.now() - s.startedAt;
        onGameOverRef.current && onGameOverRef.current(s.coinCount, Math.floor(duration));
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); gameSounds.engineStop(); gameSounds.stopMusic(); };
  }, [init, paused]);

  return (
    <div className="relative w-full max-w-[440px] mx-auto" data-testid="game-canvas-wrapper">
      <canvas
        ref={canvasRef}
        className="w-full block rounded-3xl nb-border nb-shadow"
        style={{
          height: "clamp(480px, 72vh, 720px)",
          touchAction: "none",
          background: "linear-gradient(180deg, #7DD3FC 0%, #0EA5E9 45%, #075985 100%)",
        }}
        data-testid="game-canvas"
      />
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between pointer-events-none gap-2" data-testid="game-hud">
        <div className="bg-black/70 backdrop-blur-sm border border-[rgba(0,229,255,0.5)] rounded-xl px-2.5 py-1 sm:px-3 sm:py-1.5 font-mono font-bold text-sm sm:text-lg text-[var(--jd-primary)]" style={{ boxShadow: "0 0 12px rgba(0,229,255,0.25)" }}>
          <span data-testid="hud-coins">{hudCoins}</span>{" "}
          <span className="text-[var(--jd-secondary)]">$JETDOG</span>
        </div>
        {durationSec > 0 && (
          <div
            className={`backdrop-blur-sm border rounded-xl px-2.5 py-1 sm:px-3 sm:py-1.5 font-mono font-bold text-sm sm:text-lg ${
              hudTime <= 10
                ? "bg-[var(--jd-accent)] border-[rgba(255,0,127,0.7)] text-white"
                : "bg-black/70 border-white/25 text-white"
            }`}
            style={hudTime <= 10 ? { boxShadow: "0 0 14px rgba(255,0,127,0.5)" } : undefined}
            data-testid="hud-time"
          >
            {hudTime}s
          </div>
        )}
        <div className="bg-black/70 backdrop-blur-sm border border-[rgba(255,0,127,0.5)] rounded-xl px-2.5 py-1 sm:px-3 sm:py-1.5 font-mono font-bold text-sm sm:text-lg text-[var(--jd-accent)]" style={{ boxShadow: "0 0 12px rgba(255,0,127,0.2)" }}>
          <span data-testid="hud-distance">{hudDistance}</span> m
        </div>
        <button
          type="button"
          onClick={toggleMute}
          className="pointer-events-auto bg-black/70 backdrop-blur-sm border border-white/25 rounded-xl px-2 py-1 sm:px-2.5 sm:py-1.5 flex items-center justify-center text-[var(--jd-primary)]"
          aria-label={muted ? "Unmute" : "Mute"}
          data-testid="mute-toggle"
        >
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
      </div>
      <div className="md:hidden mt-3 grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => moveLane(-1)}
          className="nb-btn nb-btn--white"
          style={{ minHeight: 64, fontSize: "1.1rem", touchAction: "manipulation" }}
          data-testid="ctrl-left"
        >
          ◀ Left
        </button>
        <button
          type="button"
          onClick={() => moveLane(1)}
          className="nb-btn nb-btn--cyan"
          style={{ minHeight: 64, fontSize: "1.1rem", touchAction: "manipulation" }}
          data-testid="ctrl-right"
        >
          Right ▶
        </button>
      </div>
      <p className="md:hidden mt-2 text-xs text-center text-[var(--jd-ocean)]">
        Tap a lane on screen, swipe left/right, or use buttons
      </p>
    </div>
  );
};

function update(s, dt) {
  s.speed = 340 + Math.min(420, s.distance * 0.3);
  s.distance += (s.speed * dt) / 16;
  s.bobPhase += dt * 8;
  s.playerX += (s.targetX - s.playerX) * Math.min(1, dt * 14);
  s.tilt *= 0.85;
  if (s.shake > 0) s.shake = Math.max(0, s.shake - dt * 1.6);
  s.camX += ((s.playerX - s.W / 2) * 0.12 - s.camX) * Math.min(1, dt * 5);

  s.spawnTimer -= dt;
  if (s.spawnTimer <= 0) {
    let lane = Math.floor(s.rng() * 3);
    // Fairness: never let all 3 lanes be blocked in the incoming window
    const incoming = s.obstacles.filter((ob) => ob.y < 220);
    const occupied = new Set(incoming.map((ob) => ob.lane));
    if (occupied.size >= 2 && !occupied.has(lane)) {
      lane = incoming[incoming.length - 1].lane;
    }
    const r = s.rng();
    const type = r < 0.28 ? "buoy" : r < 0.52 ? "rock" : r < 0.72 ? "wave" : r < 0.88 ? "barrel" : "shark";
    const radii = { buoy: 28, rock: 34, wave: 36, barrel: 27, shark: 31 };
    s.obstacles.push({ x: s.laneX[lane], y: -70, lane, type, r: radii[type], seed: s.rng() * 10, bob: s.rng() * Math.PI * 2 });
    s.spawnTimer = 0.7 + s.rng() * 0.45 - Math.min(0.35, s.distance * 0.0006);
  }
  s.coinTimer -= dt;
  if (s.coinTimer <= 0) {
    const lane = Math.floor(s.rng() * 3);
    const count = 4 + Math.floor(s.rng() * 4);
    for (let i = 0; i < count; i++) s.coins.push({ x: s.laneX[lane], y: -70 - i * 40, r: 14, spin: s.rng() * Math.PI * 2 });
    s.coinTimer = 1.0 + s.rng() * 0.6;
  }
  s.powerupTimer -= dt;
  if (s.powerupTimer <= 0) {
    const lane = Math.floor(s.rng() * 3);
    const type = s.rng() < 0.5 ? "shield" : "magnet";
    s.powerups.push({ x: s.laneX[lane], y: -70, type, r: 22, bob: s.rng() * Math.PI * 2 });
    s.powerupTimer = 9 + s.rng() * 7;
  }
  if (s.airT > 0) s.airT = Math.max(0, s.airT - dt);

  s.rampTimer -= dt;
  if (s.rampTimer <= 0) {
    const lane = Math.floor(s.rng() * 3);
    s.ramps.push({ x: s.laneX[lane], y: -70, lane, r: 30, bob: s.rng() * Math.PI * 2 });
    s.rampTimer = 7 + s.rng() * 6;
  }
  if (Math.random() < 0.6) s.droplets.push({ x: s.playerX + (Math.random() - 0.5) * 30, y: s.playerY + 24, vx: -50 + Math.random() * 100, vy: 100 + Math.random() * 200, r: 2 + Math.random() * 3, life: 1 });

  const dy = s.speed * dt;
  for (const o of s.obstacles) { o.y += dy; o.bob += dt * 3; }
  for (const c of s.coins) { c.y += dy; c.spin += dt * 6; }
  for (const p of s.powerups) { p.y += dy; p.bob += dt * 4; }
  for (const rp of s.ramps) { rp.y += dy; rp.bob += dt * 2; }
  s.obstacles = s.obstacles.filter((o) => o.y < s.H + 90);
  s.coins = s.coins.filter((c) => c.y < s.H + 40);
  s.powerups = s.powerups.filter((p) => p.y < s.H + 40);
  s.ramps = s.ramps.filter((rp) => rp.y < s.H + 60);
  if (s.shieldT > 0) s.shieldT = Math.max(0, s.shieldT - dt);
  if (s.magnetT > 0) s.magnetT = Math.max(0, s.magnetT - dt);

  for (const f of s.foam) {
    f.y += s.speed * dt * f.a;
    if (f.y > s.H + 10) { f.y = s.H * 0.24 - 10; f.x = Math.random() * s.W; }
  }
  for (const d of s.droplets) { d.x += d.vx * dt; d.y += d.vy * dt; d.vy += 280 * dt; d.life -= dt * 1.2; }
  s.droplets = s.droplets.filter((d) => d.life > 0);
  for (const b of s.bursts) { b.life += dt; for (const p of b.particles) { p.x += p.vx * dt; p.y += p.vy * dt; p.vy += 350 * dt; } }
  s.bursts = s.bursts.filter((b) => b.life < 0.6);

  const px = s.playerX, py = s.playerY, pr = 32;
  if (s.airT <= 0) {
    for (let i = s.ramps.length - 1; i >= 0; i--) {
      const rp = s.ramps[i];
      if (Math.abs(rp.x - px) < pr + rp.r && Math.abs(rp.y - py) < pr + rp.r) {
        s.airT = 0.9;
        s.ramps.splice(i, 1);
        gameSounds.jump();
        break;
      }
    }
  }
  if (s.magnetT > 0) {
    for (const c of s.coins) {
      const mdx = px - c.x, mdy = py - c.y;
      const d = Math.hypot(mdx, mdy);
      if (d < 190 && d > 1) { const pull = 480 * dt; c.x += (mdx / d) * pull; c.y += (mdy / d) * pull; }
    }
  }
  for (let i = s.powerups.length - 1; i >= 0; i--) {
    const p = s.powerups[i];
    if (Math.abs(p.x - px) < pr + p.r && Math.abs(p.y - py) < pr + p.r) {
      if (p.type === "shield") s.shieldT = 8; else s.magnetT = 6;
      const particles = [];
      const col = p.type === "shield" ? "#22D3EE" : "#F87171";
      for (let j = 0; j < 14; j++) { const a = (j / 14) * Math.PI * 2; particles.push({ x: p.x, y: p.y, vx: Math.cos(a) * 180, vy: Math.sin(a) * 180 - 60, color: col }); }
      s.bursts.push({ life: 0, particles });
      s.powerups.splice(i, 1);
      gameSounds.powerup();
    }
  }
  for (let i = s.coins.length - 1; i >= 0; i--) {
    const c = s.coins[i];
    if (Math.abs(c.x - px) < pr + c.r && Math.abs(c.y - py) < pr + c.r) {
      const particles = [];
      for (let j = 0; j < 12; j++) { const a = (j / 12) * Math.PI * 2; particles.push({ x: c.x, y: c.y, vx: Math.cos(a) * 160, vy: Math.sin(a) * 160 - 60 }); }
      s.bursts.push({ life: 0, particles });
      s.coins.splice(i, 1);
      s.coinCount += 1;
      gameSounds.coin();
    }
  }
  for (let i = s.obstacles.length - 1; i >= 0; i--) {
    if (s.airT > 0) break; // airborne — sail over obstacles
    const o = s.obstacles[i];
    const dx2 = o.x - px, dy2 = o.y - py;
    const hitR = pr * 0.7 + o.r * 0.65;
    if (dx2 * dx2 + dy2 * dy2 < hitR * hitR) {
      if (s.shieldT > 0) {
        s.shieldT = 0;
        const particles = [];
        for (let j = 0; j < 16; j++) { const a = (j / 16) * Math.PI * 2; particles.push({ x: o.x, y: o.y, vx: Math.cos(a) * 220, vy: Math.sin(a) * 220 - 80, color: "#22D3EE" }); }
        s.bursts.push({ life: 0, particles });
        s.obstacles.splice(i, 1);
        s.shake = 0.3;
        gameSounds.shieldHit();
        continue;
      }
      s.gameOver = true;
      s.shake = 0.55;
      const particles = [];
      for (let j = 0; j < 24; j++) { const a = Math.random() * Math.PI * 2; const v = 200 + Math.random() * 300; particles.push({ x: px, y: py, vx: Math.cos(a) * v, vy: Math.sin(a) * v - 100 }); }
      s.bursts.push({ life: 0, particles });
      gameSounds.crash();
      return;
    }
  }
}

// Hyperbolic perspective easing: slow far away, accelerating as it nears.
function persp(nt) {
  return nt / (3 - 2 * nt);
}

function smoothstep(a, b, x) {
  const v = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return v * v * (3 - 2 * v);
}

// Day → sunset → night → day cycle driven by run distance (~2400m per cycle).
function dayPhase(distance) {
  const cyc = (distance % 2400) / 2400;
  const sunset = smoothstep(0.3, 0.45, cyc) * (1 - smoothstep(0.55, 0.7, cyc));
  const night = smoothstep(0.55, 0.7, cyc) * (1 - smoothstep(0.9, 1, cyc));
  return { sunset, night };
}

// Pseudo-3D perspective projection: world (lane x, scroll y) → screen.
function proj(s, xWorld, yWorld) {
  const horizonY = s.H * 0.26;
  const base = s.playerY;
  const nt = (yWorld + 70) / (base + 70);
  const cx = s.W / 2 - (s.camX || 0);
  if (nt <= 0) return { x: cx + (xWorld - cx) * 0.3, y: horizonY, k: 0.2, a: 0 };
  if (nt <= 1) {
    const pt = persp(nt);
    return {
      x: cx + (xWorld - cx) * (0.3 + 0.7 * pt),
      y: horizonY + (base - horizonY) * pt,
      k: 0.2 + 0.8 * pt,
      a: Math.min(1, nt * 5),
    };
  }
  return { x: xWorld, y: yWorld, k: Math.min(1.12, 1 + (nt - 1) * 0.5), a: 1 };
}

function draw(s, rider, sprites) {
  const { ctx, W, H } = s;
  const t = s.bobPhase;
  const horizonY = H * 0.26;
  const mood = dayPhase(s.distance);

  ctx.save();
  if (s.shake > 0) {
    const m = s.shake * 9;
    ctx.translate((Math.random() - 0.5) * m, (Math.random() - 0.5) * m);
  }

  // ===== SKY + HORIZON (HD backdrop with parallax) =====
  const bg = sprites && sprites.bg;
  if (bg && bg.complete && bg.naturalWidth > 0) {
    const destH = horizonY + H * 0.02;
    const ratio = W / destH;
    // crop away most of the image's own water so its horizon sits at the seam
    let srcH = bg.naturalHeight * 0.68;
    let srcW = srcH * ratio;
    if (srcW > bg.naturalWidth) { srcW = bg.naturalWidth; srcH = srcW / ratio; }
    const margin = (bg.naturalWidth - srcW) / 2;
    const sway = Math.sin(t * 0.03) * Math.min(30, margin * 0.3);
    const pan = Math.max(-margin, Math.min(margin, (s.camX || 0) * 1.6 + sway));
    const sx = margin + pan;
    ctx.drawImage(bg, sx, 0, srcW, srcH, 0, 0, W, destH);
  } else {
    const sky = ctx.createLinearGradient(0, 0, 0, horizonY);
    sky.addColorStop(0, "#BFE9FF");
    sky.addColorStop(1, "#FFE2B5");
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, W, horizonY);
  }

  // ===== DAY/NIGHT MOOD over the sky =====
  const skyH = horizonY + H * 0.02;
  if (mood.sunset > 0.01) {
    const sg = ctx.createLinearGradient(0, 0, 0, skyH);
    sg.addColorStop(0, `rgba(110, 55, 150, ${0.35 * mood.sunset})`);
    sg.addColorStop(0.6, `rgba(255, 110, 60, ${0.45 * mood.sunset})`);
    sg.addColorStop(1, `rgba(255, 175, 85, ${0.5 * mood.sunset})`);
    ctx.fillStyle = sg;
    ctx.fillRect(0, 0, W, skyH);
  }
  if (mood.night > 0.01) {
    ctx.fillStyle = `rgba(8, 14, 46, ${0.72 * mood.night})`;
    ctx.fillRect(0, 0, W, skyH);
    // Twinkling stars
    for (let i = 0; i < 26; i++) {
      const sxr = ((i * 73) % 97) / 97;
      const syr = ((i * 41) % 89) / 89;
      const tw = Math.sin(t * 1.4 + i * 1.7) * 0.5 + 0.5;
      ctx.globalAlpha = mood.night * (0.3 + 0.7 * tw);
      ctx.fillStyle = "#FFFFFF";
      ctx.beginPath();
      ctx.arc(sxr * W, syr * skyH * 0.85, i % 3 === 0 ? 1.8 : 1.1, 0, Math.PI * 2);
      ctx.fill();
    }
    // Moon with glow
    ctx.globalAlpha = mood.night;
    const mx = W * 0.24, my = H * 0.08;
    const mg = ctx.createRadialGradient(mx, my, 3, mx, my, 42);
    mg.addColorStop(0, "rgba(240,245,255,0.9)");
    mg.addColorStop(0.4, "rgba(220,230,255,0.35)");
    mg.addColorStop(1, "rgba(220,230,255,0)");
    ctx.fillStyle = mg;
    ctx.fillRect(mx - 42, my - 42, 84, 84);
    ctx.fillStyle = "#F2F6FF";
    ctx.beginPath(); ctx.arc(mx, my, 14, 0, Math.PI * 2); ctx.fill();
    ctx.fillStyle = "rgba(190,205,235,0.7)";
    ctx.beginPath();
    ctx.arc(mx - 4, my - 3, 3, 0, Math.PI * 2);
    ctx.arc(mx + 5, my + 4, 2.2, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 1;
  }

  // ===== BIRDS over the backdrop =====
  for (const bd of s.birds) {
    bd.x -= bd.speed * 0.016;
    bd.phase += 0.08;
    if (bd.x < -30) bd.x = W + 20;
    drawBird(ctx, bd.x, bd.y + Math.sin(bd.phase) * 4, bd.phase);
  }

  // ===== WATER BODY =====
  const water = ctx.createLinearGradient(0, horizonY, 0, H);
  water.addColorStop(0, "#45D6F2");
  water.addColorStop(0.16, "#1CB2E4");
  water.addColorStop(0.5, "#0B74B2");
  water.addColorStop(1, "#083A66");
  ctx.fillStyle = water;
  ctx.fillRect(0, horizonY, W, H - horizonY);

  // Atmospheric haze at the horizon (depth cue)
  const haze = ctx.createLinearGradient(0, horizonY, 0, horizonY + H * 0.14);
  haze.addColorStop(0, "rgba(255,255,255,0.5)");
  haze.addColorStop(1, "rgba(255,255,255,0)");
  ctx.fillStyle = haze;
  ctx.fillRect(0, horizonY, W, H * 0.14);

  // Shimmering horizon light line
  ctx.globalAlpha = (0.55 + Math.sin(t * 0.9) * 0.2) * (1 - mood.night * 0.6);
  ctx.strokeStyle = "rgba(255,255,255,0.9)";
  ctx.lineWidth = 1.5;
  ctx.setLineDash([10, 14]);
  ctx.lineDashOffset = -(t * 6) % 24;
  ctx.beginPath(); ctx.moveTo(0, horizonY + 1); ctx.lineTo(W, horizonY + 1); ctx.stroke();
  ctx.setLineDash([]);
  ctx.globalAlpha = 1;

  // ===== DAY/NIGHT MOOD over the water =====
  if (mood.sunset > 0.01) {
    ctx.fillStyle = `rgba(255, 120, 60, ${0.16 * mood.sunset})`;
    ctx.fillRect(0, horizonY, W, H - horizonY);
  }
  if (mood.night > 0.01) {
    ctx.fillStyle = `rgba(4, 8, 34, ${0.45 * mood.night})`;
    ctx.fillRect(0, horizonY, W, H - horizonY);
  }

  // Sun reflection shimmer column
  const sunX = W * 0.76;
  ctx.save();
  ctx.globalAlpha = (0.5 + Math.sin(t * 0.8) * 0.12) * (1 - mood.night * 0.85);
  const refl = ctx.createLinearGradient(0, horizonY, 0, H * 0.62);
  refl.addColorStop(0, "rgba(255, 226, 150, 0.5)");
  refl.addColorStop(1, "rgba(255, 226, 150, 0)");
  ctx.fillStyle = refl;
  ctx.beginPath();
  ctx.moveTo(sunX - 26, horizonY);
  ctx.lineTo(sunX + 26, horizonY);
  ctx.lineTo(sunX + 90, H * 0.62);
  ctx.lineTo(sunX - 90, H * 0.62);
  ctx.closePath();
  ctx.fill();
  ctx.restore();

  // ===== PERSPECTIVE WATER BANDS (3D depth) =====
  for (const b of s.bands) {
    const wt = (b.o + s.distance * 0.01) % 1;
    const pt = persp(wt);
    const by = horizonY + (H - horizonY) * pt;
    ctx.globalAlpha = 0.04 + 0.2 * pt;
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 0.8 + 2.4 * pt;
    ctx.beginPath();
    for (let x = 0; x <= W; x += 14) {
      const yy = by + Math.sin(x * 0.02 + t * 0.6 + b.o * 9) * (1.5 + 6 * pt);
      if (x === 0) ctx.moveTo(x, yy); else ctx.lineTo(x, yy);
    }
    ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // ===== SPEED STREAKS (perspective rays) =====
  const cx = W / 2 - (s.camX || 0);
  for (const st of s.streaks) {
    const wt = (st.o + s.distance * 0.02 * st.sp) % 1;
    const p1 = persp(wt);
    const p2 = persp(Math.min(1, wt + 0.05));
    const x1 = cx + st.xf * W * (0.3 + 0.7 * p1);
    const x2 = cx + st.xf * W * (0.3 + 0.7 * p2);
    const y1 = horizonY + (H - horizonY) * p1;
    const y2 = horizonY + (H - horizonY) * p2;
    ctx.globalAlpha = 0.08 + 0.24 * p1;
    ctx.strokeStyle = "#EAF9FF";
    ctx.lineWidth = 1 + 1.8 * p1;
    ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // ===== LANE GUIDE LINES (converging to vanishing point) =====
  for (let i = 0; i < s.laneCount - 1; i++) {
    const xb = (s.laneX[i] + s.laneX[i + 1]) / 2;
    const lg = ctx.createLinearGradient(0, horizonY, 0, H);
    lg.addColorStop(0, "rgba(255,255,255,0.02)");
    lg.addColorStop(1, "rgba(255,255,255,0.30)");
    ctx.strokeStyle = lg;
    ctx.setLineDash([16, 22]);
    ctx.lineWidth = 2.5;
    ctx.lineDashOffset = -(s.distance % 38);
    ctx.beginPath();
    ctx.moveTo(cx + (xb - cx) * 0.3, horizonY);
    ctx.lineTo(cx + (xb - cx) * 1.28, H);
    ctx.stroke();
  }
  ctx.setLineDash([]);

  // ===== EDGE MARKER BUOYS (red left / green right, slalom course style) =====
  const laneGap = s.laneX[1] - s.laneX[0];
  const edgesX = [s.laneX[0] - laneGap * 0.72, s.laneX[2] + laneGap * 0.72];
  for (let side = 0; side < 2; side++) {
    for (let m = 0; m < 8; m++) {
      const wt = (m / 8 + s.distance * 0.01) % 1;
      const pt = persp(wt);
      const bx = cx + (edgesX[side] - cx) * (0.3 + 0.7 * pt);
      const by = horizonY + (H - horizonY) * pt;
      const br = 3 + 8 * pt;
      ctx.globalAlpha = Math.min(1, wt * 5);
      // reflection
      ctx.fillStyle = "rgba(0, 20, 40, 0.25)";
      ctx.beginPath(); ctx.ellipse(bx, by + br * 0.9, br * 0.9, br * 0.28, 0, 0, Math.PI * 2); ctx.fill();
      // body
      ctx.fillStyle = side === 0 ? "#E5484D" : "#30A46C";
      ctx.beginPath(); ctx.arc(bx, by, br, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = "#0A2540";
      ctx.lineWidth = Math.max(1, 1.6 * pt);
      ctx.stroke();
      // white band + highlight
      ctx.fillStyle = "#FFFFFF";
      ctx.fillRect(bx - br * 0.85, by - br * 0.22, br * 1.7, br * 0.44);
      ctx.beginPath(); ctx.arc(bx - br * 0.35, by - br * 0.45, br * 0.2, 0, Math.PI * 2); ctx.fill();
    }
  }
  ctx.globalAlpha = 1;

  // ===== FLOTSAM (foam patches filling the open sea) =====
  for (const fl of s.flotsam) {
    const wt = (fl.o + s.distance * 0.009) % 1;
    const pt = persp(wt);
    const fx = cx + fl.xf * (W / 2) * (0.3 + 0.7 * pt) * 2;
    const fy = horizonY + (H - horizonY) * pt;
    const fr = fl.r * (0.2 + 0.8 * pt);
    ctx.globalAlpha = 0.06 + 0.22 * pt;
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath();
    ctx.ellipse(fx, fy, fr, fr * 0.32, 0, 0, Math.PI * 2);
    ctx.ellipse(fx - fr * 0.7, fy + fr * 0.15, fr * 0.5, fr * 0.18, 0, 0, Math.PI * 2);
    ctx.ellipse(fx + fr * 0.75, fy - fr * 0.1, fr * 0.45, fr * 0.16, 0, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  for (const g of s.glints) {
    const wt = (g.o + s.distance * 0.008) % 1;
    const pt = persp(wt);
    const gy = horizonY + (H - horizonY) * pt;
    const gx = cx + g.xf * W * 0.5 * (0.3 + 0.7 * pt);
    g.tw += 0.06;
    ctx.globalAlpha = (Math.sin(g.tw) * 0.5 + 0.5) * (0.12 + 0.55 * pt);
    ctx.strokeStyle = "#FFFFFF";
    ctx.lineWidth = 0.8 + 1.2 * pt;
    const len = 3 + 10 * pt;
    ctx.beginPath(); ctx.moveTo(gx - len, gy); ctx.lineTo(gx + len, gy); ctx.stroke();
  }
  ctx.globalAlpha = 1;

  // ===== FOAM PARTICLES (scaled by depth) =====
  for (const f of s.foam) {
    const depth = Math.max(0, (f.y - horizonY) / (H - horizonY));
    ctx.globalAlpha = f.a * (0.25 + 0.5 * depth);
    ctx.fillStyle = "#FFFFFF";
    ctx.beginPath(); ctx.arc(f.x, f.y, f.r * (0.35 + 0.75 * depth), 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalAlpha = 1;

  // ===== OBSTACLES, RAMPS, COINS & POWER-UPS (far → near) =====
  const ordered = [...s.obstacles].sort((a, b2) => a.y - b2.y);
  for (const rp of s.ramps) drawRamp(ctx, s, rp, sprites);
  for (const o of ordered) drawObstacle(ctx, s, o, t, sprites);
  for (const c of s.coins) drawCoin(ctx, s, c, sprites);
  for (const p of s.powerups) drawPowerup(ctx, s, p);

  // ===== DROPLETS =====
  for (const d of s.droplets) {
    ctx.globalAlpha = Math.max(0, d.life) * 0.85;
    ctx.fillStyle = "#ffffff"; ctx.beginPath(); ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalAlpha = 1;

  // ===== PLAYER =====
  drawPlayer(ctx, s, rider);
  drawStatusPills(ctx, s);

  // ===== BURSTS (coin pickup / wipeout) =====
  for (const b of s.bursts) {
    const alpha = 1 - b.life / 0.6;
    for (const p of b.particles) {
      ctx.globalAlpha = Math.max(0, alpha);
      ctx.fillStyle = p.color || "#FFD700";
      ctx.beginPath(); ctx.arc(p.x, p.y, 4, 0, Math.PI * 2); ctx.fill();
    }
  }
  ctx.globalAlpha = 1;

  // ===== NIGHT MOOD tint (cohesive scene) =====
  if (mood.night > 0.01) {
    ctx.fillStyle = `rgba(10, 16, 60, ${0.18 * mood.night})`;
    ctx.fillRect(0, 0, W, H);
  }

  // ===== VIGNETTE (cinematic depth) =====
  const vig = ctx.createRadialGradient(W / 2, H * 0.55, Math.min(W, H) * 0.45, W / 2, H * 0.55, Math.max(W, H) * 0.78);
  vig.addColorStop(0, "rgba(0,20,40,0)");
  vig.addColorStop(1, "rgba(0,20,40,0.32)");
  ctx.fillStyle = vig;
  ctx.fillRect(0, 0, W, H);

  if (s.gameOver) drawGameOver(ctx, s);
  ctx.restore();
}

function drawBird(ctx, x, y, phase) {
  const flap = Math.sin(phase) * 4;
  ctx.strokeStyle = "rgba(20, 30, 50, 0.55)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(x - 8, y + flap * 0.3);
  ctx.quadraticCurveTo(x - 4, y - 4 + flap, x, y);
  ctx.quadraticCurveTo(x + 4, y - 4 + flap, x + 8, y + flap * 0.3);
  ctx.stroke();
}

function drawRamp(ctx, s, rp, sprites) {
  const P = proj(s, rp.x, rp.y);
  if (P.a <= 0.02) return;
  const k = P.k;
  const bob = Math.sin(rp.bob) * 3 * k;
  const x = P.x, y = P.y + bob * 0.5;
  const r = rp.r * k;
  ctx.globalAlpha = P.a;
  // Shadow + foam ring
  ctx.fillStyle = "rgba(0, 18, 36, 0.3)";
  ctx.beginPath(); ctx.ellipse(x, y + r * 0.7, r * 1.1, r * 0.2, 0, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.beginPath(); ctx.ellipse(x, y + r * 0.62, r * 1.25, r * 0.22, 0, 0, Math.PI * 2); ctx.fill();
  const img = sprites && sprites.ramp;
  if (img && img.complete && img.naturalWidth > 0) {
    const w = r * 2.6;
    const h = w * (img.naturalHeight / img.naturalWidth);
    ctx.drawImage(img, x - w / 2, y - h * 0.62, w, h);
  } else {
    // fallback: striped triangle ramp
    ctx.fillStyle = "#FFC53D";
    ctx.beginPath();
    ctx.moveTo(x - r, y + r * 0.5);
    ctx.lineTo(x, y - r * 0.9);
    ctx.lineTo(x + r, y + r * 0.5);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "#0A2540"; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = "#E5484D";
    ctx.fillRect(x - r * 0.55, y - r * 0.1, r * 1.1, r * 0.2);
  }
  ctx.globalAlpha = 1;
}

function drawObstacle(ctx, s, o, t, sprites) {
  const P = proj(s, o.x, o.y);
  if (P.a <= 0.02) return;
  const k = P.k;
  const bob = Math.sin(o.bob) * 4 * k;
  const x = P.x + bob * 0.5;
  const y = P.y + (o.type === "rock" ? bob * 0.3 : bob * 0.6);
  const r = o.r * k;
  const img = sprites && sprites[o.type];
  ctx.globalAlpha = P.a;
  if (img && img.complete && img.naturalWidth > 0) {
    // Water ripple rings
    ctx.strokeStyle = "rgba(255,255,255,0.55)";
    ctx.lineWidth = 1.5 * k;
    for (let i = 0; i < 2; i++) {
      const rad = r * (1.35 + i * 0.4) + Math.sin(t * 1.5 + i + o.seed) * 2 * k;
      ctx.beginPath();
      ctx.ellipse(x, y + r * 0.72, rad, rad * 0.22, 0, 0, Math.PI * 2);
      ctx.stroke();
    }
    // Shadow on water
    ctx.fillStyle = "rgba(0, 18, 36, 0.32)";
    ctx.beginPath();
    ctx.ellipse(x, y + r * 0.7, r * 1.05, r * 0.2, 0, 0, Math.PI * 2);
    ctx.fill();
    const w = r * 2.7;
    const h = w * (img.naturalHeight / img.naturalWidth);
    ctx.drawImage(img, x - w / 2, y - h * 0.6, w, h);
    return;
  }
  if (o.type === "buoy" || o.type === "barrel") drawBuoy(ctx, x, y, r, t);
  else if (o.type === "rock") drawRock(ctx, x, y, r, o.seed);
  else drawStormWave(ctx, x, y, r, o.seed, t);
  ctx.globalAlpha = 1;
}

function drawBuoy(ctx, x, y, r, t) {
  // Water ripple under buoy
  ctx.strokeStyle = "rgba(255,255,255,0.6)";
  ctx.lineWidth = 1.5;
  for (let i = 0; i < 2; i++) {
    const rad = r * (1.3 + i * 0.35) + Math.sin(t * 1.5 + i) * 2;
    ctx.beginPath();
    ctx.ellipse(x, y + r * 0.65, rad, rad * 0.22, 0, 0, Math.PI * 2);
    ctx.stroke();
  }
  // Shadow on water
  ctx.fillStyle = "rgba(0, 20, 40, 0.35)";
  ctx.beginPath();
  ctx.ellipse(x, y + r * 0.6, r * 0.85, r * 0.18, 0, 0, Math.PI * 2);
  ctx.fill();
  // Reflection highlight in water
  ctx.fillStyle = "rgba(230, 60, 70, 0.35)";
  ctx.beginPath();
  ctx.ellipse(x, y + r * 0.95, r * 0.55, r * 0.12, 0, 0, Math.PI * 2);
  ctx.fill();
  // Splash ring at waterline
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.beginPath(); ctx.ellipse(x, y + r * 0.45, r * 1.1, r * 0.18, 0, 0, Math.PI * 2); ctx.fill();
  // Body with strong shading
  const bg = ctx.createRadialGradient(x - r * 0.35, y - r * 0.45, 2, x, y, r * 1.25);
  bg.addColorStop(0, "#FFA8A0");
  bg.addColorStop(0.35, "#FF5A4D");
  bg.addColorStop(0.7, "#D62828");
  bg.addColorStop(1, "#6B0F1A");
  ctx.fillStyle = bg;
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
  // White & gold bands
  ctx.fillStyle = "#ffffff"; ctx.fillRect(x - r, y - 4, r * 2, 8);
  ctx.fillStyle = "#FFD700"; ctx.fillRect(x - r * 0.95, y - 11, r * 1.9, 4);
  // Top antenna
  ctx.strokeStyle = "#0A2540"; ctx.lineWidth = 3;
  ctx.beginPath(); ctx.moveTo(x, y - r); ctx.lineTo(x, y - r - 16); ctx.stroke();
  // Blinking light
  const blink = (Math.sin(t * 6) * 0.5 + 0.5);
  ctx.fillStyle = `rgba(255, 235, 59, ${0.5 + blink * 0.5})`;
  ctx.beginPath(); ctx.arc(x, y - r - 18, 4 + blink, 0, Math.PI * 2); ctx.fill();
  ctx.fillStyle = "#FFEB3B";
  ctx.beginPath(); ctx.arc(x, y - r - 18, 3, 0, Math.PI * 2); ctx.fill();
  // Outline
  ctx.strokeStyle = "#0A2540"; ctx.lineWidth = 2.5;
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.stroke();
  // Specular highlight
  ctx.fillStyle = "rgba(255,255,255,0.6)";
  ctx.beginPath(); ctx.ellipse(x - r * 0.4, y - r * 0.5, r * 0.22, r * 0.12, -0.5, 0, Math.PI * 2); ctx.fill();
}

function drawRock(ctx, x, y, r, seed) {
  // Shadow on water
  ctx.fillStyle = "rgba(0, 15, 30, 0.4)";
  ctx.beginPath(); ctx.ellipse(x, y + r * 0.65, r * 1.05, r * 0.22, 0, 0, Math.PI * 2); ctx.fill();
  // Water lapping ring
  ctx.fillStyle = "rgba(255,255,255,0.8)";
  ctx.beginPath(); ctx.ellipse(x, y + r * 0.5, r * 1.2, r * 0.24, 0, 0, Math.PI * 2); ctx.fill();
  // Rock body — irregular polygon with gradient
  const grad = ctx.createRadialGradient(x - r * 0.35, y - r * 0.45, 2, x, y, r * 1.25);
  grad.addColorStop(0, "#8B95A3");
  grad.addColorStop(0.45, "#4B5563");
  grad.addColorStop(0.85, "#1F2937");
  grad.addColorStop(1, "#0A0F1A");
  ctx.fillStyle = grad;
  ctx.beginPath();
  const N = 11;
  for (let i = 0; i < N; i++) {
    const a = (i / N) * Math.PI * 2 + seed;
    const radius = r * (0.82 + Math.sin(seed * 2 + i * 1.9) * 0.28);
    const px = x + Math.cos(a) * radius;
    const py = y + Math.sin(a) * radius * 0.85;
    if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
  }
  ctx.closePath(); ctx.fill();
  ctx.strokeStyle = "#0A2540"; ctx.lineWidth = 2.5; ctx.stroke();
  // Crack details
  ctx.strokeStyle = "rgba(0,0,0,0.35)";
  ctx.lineWidth = 1.2;
  ctx.beginPath();
  ctx.moveTo(x - r * 0.4, y - r * 0.1);
  ctx.lineTo(x + r * 0.1, y + r * 0.25);
  ctx.lineTo(x - r * 0.1, y + r * 0.5);
  ctx.stroke();
  // Moss/seaweed tuft on top
  ctx.fillStyle = "#3F7D4B";
  ctx.beginPath();
  ctx.ellipse(x - r * 0.2, y - r * 0.55, r * 0.45, r * 0.18, -0.3, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#5BA363";
  ctx.beginPath();
  ctx.ellipse(x + r * 0.05, y - r * 0.62, r * 0.18, r * 0.1, 0.1, 0, Math.PI * 2);
  ctx.fill();
  // Seaweed strands
  ctx.strokeStyle = "#2A5A33";
  ctx.lineWidth = 1.5;
  for (let k = 0; k < 3; k++) {
    const sx = x - r * 0.3 + k * r * 0.25;
    ctx.beginPath();
    ctx.moveTo(sx, y - r * 0.45);
    ctx.quadraticCurveTo(sx + 4, y - r * 0.7, sx + 2, y - r * 0.95);
    ctx.stroke();
  }
  // Barnacles (small dots)
  ctx.fillStyle = "#D6D3C7";
  for (let k = 0; k < 6; k++) {
    const a = seed + k * 1.3;
    const bx = x + Math.cos(a) * r * 0.55;
    const by = y + Math.sin(a) * r * 0.45;
    ctx.beginPath(); ctx.arc(bx, by, 2.2, 0, Math.PI * 2); ctx.fill();
  }
  // Highlight
  ctx.fillStyle = "rgba(255,255,255,0.32)";
  ctx.beginPath();
  ctx.ellipse(x - r * 0.45, y - r * 0.35, r * 0.22, r * 0.12, -0.6, 0, Math.PI * 2);
  ctx.fill();
}

function drawStormWave(ctx, x, y, r, seed, t) {
  // Shadow on water
  ctx.fillStyle = "rgba(0, 15, 30, 0.25)";
  ctx.beginPath();
  ctx.ellipse(x, y + r * 0.55, r * 1.2, r * 0.22, 0, 0, Math.PI * 2);
  ctx.fill();
  // Base foam wake
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.beginPath();
  ctx.ellipse(x, y + r * 0.55, r * 1.25, r * 0.28, 0, 0, Math.PI * 2);
  ctx.fill();
  // Main wave body — translucent layered
  const grad = ctx.createLinearGradient(x, y - r, x, y + r);
  grad.addColorStop(0, "#B7E4FA");
  grad.addColorStop(0.4, "#22B5E8");
  grad.addColorStop(0.85, "#075985");
  grad.addColorStop(1, "#062E55");
  ctx.fillStyle = grad;
  ctx.beginPath();
  const curl = Math.sin(t * 2 + seed) * 0.1;
  ctx.moveTo(x - r, y + r * 0.3);
  ctx.quadraticCurveTo(x - r * 0.8, y - r * 1.2, x + curl * r, y - r * 0.8);
  ctx.quadraticCurveTo(x + r * 0.4, y - r * 0.4, x + r * 0.65, y + r * 0.1);
  ctx.quadraticCurveTo(x + r * 0.4, y + r * 0.45, x + r, y + r * 0.3);
  ctx.closePath();
  ctx.fill();
  ctx.strokeStyle = "#0A2540"; ctx.lineWidth = 2; ctx.stroke();
  // Foam crest — multiple dots along the lip
  ctx.fillStyle = "#ffffff";
  for (let k = 0; k < 6; k++) {
    const fx = x - r * 0.6 + k * r * 0.25 + Math.sin(seed + k) * 4;
    const fy = y - r * 0.85 + Math.cos(seed + k * 0.7) * 6;
    ctx.beginPath(); ctx.arc(fx, fy, 3 + Math.sin(t + k) * 0.6, 0, Math.PI * 2); ctx.fill();
  }
  // Curl highlight
  ctx.fillStyle = "rgba(255,255,255,0.85)";
  ctx.beginPath();
  ctx.ellipse(x - r * 0.25, y - r * 0.75, r * 0.55, r * 0.18, -0.4, 0, Math.PI * 2);
  ctx.fill();
  // Inner shadow under curl
  ctx.fillStyle = "rgba(7, 89, 133, 0.55)";
  ctx.beginPath();
  ctx.ellipse(x - r * 0.1, y - r * 0.35, r * 0.42, r * 0.12, -0.2, 0, Math.PI * 2);
  ctx.fill();
  // Splash droplets above
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  for (let k = 0; k < 3; k++) {
    ctx.beginPath();
    ctx.arc(x + (k - 1) * 8, y - r - 4 - k * 3, 2.5, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawCoin(ctx, s, c, sprites) {
  const P = proj(s, c.x, c.y);
  if (P.a <= 0.02) return;
  const x = P.x, y = P.y, r = c.r * P.k;
  const spin = c.spin;
  const w = Math.abs(Math.cos(spin));
  ctx.globalAlpha = P.a;
  const img = sprites && sprites.coin;
  if (img && img.complete && img.naturalWidth > 0) {
    // Glow halo
    const halo = ctx.createRadialGradient(x, y, 4, x, y, r * 2.1);
    halo.addColorStop(0, "rgba(255, 215, 0, 0.4)");
    halo.addColorStop(1, "rgba(255, 215, 0, 0)");
    ctx.fillStyle = halo;
    ctx.beginPath(); ctx.arc(x, y, r * 2.1, 0, Math.PI * 2); ctx.fill();
    // Shadow on water
    ctx.fillStyle = "rgba(0, 15, 30, 0.25)";
    ctx.beginPath();
    ctx.ellipse(x, y + r + 6, r * 0.8, 4, 0, 0, Math.PI * 2);
    ctx.fill();
    // Spin: squash horizontally
    const dw = r * 2.4 * (0.18 + 0.82 * w);
    const dh = r * 2.4;
    ctx.drawImage(img, x - dw / 2, y - dh / 2, dw, dh);
    ctx.globalAlpha = 1;
    return;
  }
  const halfW = r * w + r * 0.15, halfH = r;
  // Glow halo
  const halo = ctx.createRadialGradient(x, y, 4, x, y, r * 2.2);
  halo.addColorStop(0, "rgba(255, 215, 0, 0.45)");
  halo.addColorStop(1, "rgba(255, 215, 0, 0)");
  ctx.fillStyle = halo;
  ctx.beginPath(); ctx.arc(x, y, r * 2.2, 0, Math.PI * 2); ctx.fill();
  // Shadow on water
  ctx.fillStyle = "rgba(0, 15, 30, 0.28)";
  ctx.beginPath();
  ctx.ellipse(x, y + r + 6, halfW * 0.7, 4, 0, 0, Math.PI * 2);
  ctx.fill();
  // Rim edge (darker gold)
  ctx.fillStyle = "#7A5A14";
  ctx.beginPath(); ctx.ellipse(x, y, halfW, halfH, 0, 0, Math.PI * 2); ctx.fill();
  // Inner face with metallic gradient
  const innerW = Math.max(0.5, halfW - 3), innerH = Math.max(0.5, halfH - 3);
  if (innerW > 1) {
    const grad = ctx.createRadialGradient(x - innerW * 0.35, y - innerH * 0.4, 2, x, y, innerW * 1.4);
    grad.addColorStop(0, "#FFFBEA");
    grad.addColorStop(0.4, "#FFE066");
    grad.addColorStop(0.75, "#FFB800");
    grad.addColorStop(1, "#9C7414");
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.ellipse(x, y, innerW, innerH, 0, 0, Math.PI * 2); ctx.fill();
  }
  // J emboss
  if (w > 0.35) {
    ctx.fillStyle = "rgba(122, 85, 18, 0.85)";
    ctx.font = `bold ${Math.floor(r * 1.15)}px Anton, sans-serif`;
    ctx.textAlign = "center"; ctx.textBaseline = "middle";
    ctx.fillText("J", x, y + 1);
    // Highlight on letter
    ctx.fillStyle = "rgba(255, 245, 200, 0.7)";
    ctx.fillText("J", x - 0.6, y + 0.4);
    ctx.fillStyle = "rgba(122, 85, 18, 0.95)";
    ctx.fillText("J", x, y + 1);
  }
  // Specular highlight arc
  if (w > 0.45) {
    ctx.strokeStyle = "rgba(255,255,255,0.85)";
    ctx.lineWidth = 1.6;
    ctx.beginPath();
    ctx.ellipse(x, y, innerW * 0.78, innerH * 0.78, 0, -2.5, -1.3);
    ctx.stroke();
  }
  // Outline
  ctx.strokeStyle = "#0A2540";
  ctx.lineWidth = 2;
  ctx.beginPath(); ctx.ellipse(x, y, halfW, halfH, 0, 0, Math.PI * 2); ctx.stroke();
  ctx.globalAlpha = 1;
}

function drawPlayer(ctx, s, rider) {
  const bob = Math.sin(s.bobPhase) * 3;
  const px = s.playerX;
  const py = s.playerY + bob;
  const jumpP = s.airT > 0 ? 1 - s.airT / 0.9 : 0;
  const jump = jumpP > 0 ? Math.sin(Math.PI * jumpP) : 0;
  const lift = jump * 80;

  // Spray particles (behind = below the player) — hidden mid-air
  ctx.globalAlpha = 1 - jump;
  ctx.fillStyle = "rgba(255,255,255,0.55)";
  for (let i = 0; i < 10; i++) {
    const angle = Math.PI / 2 + (Math.random() - 0.5) * 0.4;
    const dist = 28 + (i / 10) * 60;
    const sx = px + Math.cos(angle) * dist;
    const sy = py + Math.sin(angle) * dist;
    ctx.beginPath();
    ctx.arc(sx, sy, 2 + Math.random() * 2.5, 0, Math.PI * 2);
    ctx.fill();
  }

  // Wake trail below
  const wakeGrad = ctx.createLinearGradient(px, py + 110, px, py);
  wakeGrad.addColorStop(0, "rgba(255,255,255,0)");
  wakeGrad.addColorStop(1, "rgba(255,255,255,0.75)");
  ctx.fillStyle = wakeGrad;
  ctx.beginPath();
  ctx.ellipse(px, py + 65, 14, 60, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;

  // Shadow under rider (stays on the water, shrinks while airborne)
  ctx.fillStyle = `rgba(0,0,0,${0.3 * (1 - 0.55 * jump)})`;
  ctx.beginPath();
  ctx.ellipse(px, s.playerY + 36, 42 * (1 - 0.4 * jump), 8 * (1 - 0.3 * jump), 0, 0, Math.PI * 2);
  ctx.fill();

  ctx.save();
  ctx.translate(px, py + bob * 0.3 - lift);
  ctx.rotate(s.tilt - jump * 0.08);
  ctx.scale(1 + 0.3 * jump, 1 + 0.3 * jump);
  if (rider && rider.complete && rider.naturalWidth > 0) {
    // Rear-view sprite (Subway Surfers style) — sized for narrow portrait canvas
    const drawW = Math.max(72, Math.min(108, s.W * 0.24));
    const aspect = rider.naturalHeight / rider.naturalWidth;
    const drawH = drawW * aspect;
    ctx.drawImage(rider, -drawW / 2, -drawH * 0.55, drawW, drawH);
  } else {
    // Fallback while loading
    ctx.fillStyle = "#FFD93D";
    ctx.strokeStyle = "#0A2540";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.ellipse(0, 12, 50, 12, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();

  // Shield bubble
  if (s.shieldT > 0) {
    const blink = s.shieldT < 2 ? (Math.sin(s.bobPhase * 5) * 0.5 + 0.5) : 1;
    ctx.globalAlpha = 0.25 + 0.45 * blink;
    const bub = ctx.createRadialGradient(px, py, 20, px, py, 62);
    bub.addColorStop(0, "rgba(34, 211, 238, 0)");
    bub.addColorStop(0.8, "rgba(34, 211, 238, 0.25)");
    bub.addColorStop(1, "rgba(34, 211, 238, 0.6)");
    ctx.fillStyle = bub;
    ctx.beginPath(); ctx.arc(px, py, 62, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = "rgba(103, 232, 249, 0.9)";
    ctx.lineWidth = 2.5;
    ctx.beginPath(); ctx.arc(px, py, 62, 0, Math.PI * 2); ctx.stroke();
    ctx.globalAlpha = 1;
  }
  // Magnet radius ring
  if (s.magnetT > 0) {
    const blink = s.magnetT < 1.5 ? (Math.sin(s.bobPhase * 5) * 0.5 + 0.5) : 1;
    ctx.globalAlpha = 0.2 + 0.3 * blink;
    ctx.strokeStyle = "#FFD700";
    ctx.setLineDash([8, 10]);
    ctx.lineWidth = 2;
    ctx.beginPath(); ctx.arc(px, py, 95 + Math.sin(s.bobPhase * 2) * 6, 0, Math.PI * 2); ctx.stroke();
    ctx.setLineDash([]);
    ctx.globalAlpha = 1;
  }
}

function drawStatusPills(ctx, s) {
  let x = 12;
  const y = 52;
  const pill = (secs, color) => {
    const w = 56;
    ctx.fillStyle = "rgba(255,255,255,0.92)";
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(x, y, w, 26, 13); else ctx.rect(x, y, w, 26);
    ctx.fill();
    ctx.strokeStyle = "#0A2540"; ctx.lineWidth = 2; ctx.stroke();
    ctx.fillStyle = color;
    ctx.beginPath(); ctx.arc(x + 15, y + 13, 7, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = "#0A2540"; ctx.lineWidth = 1.5; ctx.stroke();
    ctx.fillStyle = "#0A2540";
    ctx.font = "bold 13px Outfit, sans-serif";
    ctx.textAlign = "left"; ctx.textBaseline = "middle";
    ctx.fillText(`${Math.ceil(secs)}s`, x + 27, y + 14);
    x += w + 8;
  };
  if (s.shieldT > 0) pill(s.shieldT, "#22D3EE");
  if (s.magnetT > 0) pill(s.magnetT, "#FFD700");
}

function drawPowerup(ctx, s, p) {
  const P = proj(s, p.x, p.y);
  if (P.a <= 0.02) return;
  const k = P.k;
  const bob = Math.sin(p.bob) * 4 * k;
  ctx.save();
  ctx.globalAlpha = P.a;
  ctx.translate(P.x, P.y + bob);
  ctx.scale(k, k);
  const x = 0, y = 0;
  const glow = p.type === "shield" ? "rgba(34,211,238,0.5)" : "rgba(248,113,113,0.5)";
  const halo = ctx.createRadialGradient(x, y, 4, x, y, p.r * 2.2);
  halo.addColorStop(0, glow);
  halo.addColorStop(1, "rgba(0,0,0,0)");
  ctx.fillStyle = halo;
  ctx.beginPath(); ctx.arc(x, y, p.r * 2.2, 0, Math.PI * 2); ctx.fill();
  // Bubble
  ctx.fillStyle = "rgba(255,255,255,0.95)";
  ctx.beginPath(); ctx.arc(x, y, p.r, 0, Math.PI * 2); ctx.fill();
  ctx.strokeStyle = "#0A2540"; ctx.lineWidth = 2.5;
  ctx.beginPath(); ctx.arc(x, y, p.r, 0, Math.PI * 2); ctx.stroke();
  if (p.type === "shield") {
    ctx.fillStyle = "#22D3EE";
    ctx.beginPath();
    ctx.moveTo(x, y - 11);
    ctx.lineTo(x + 9, y - 7);
    ctx.quadraticCurveTo(x + 9, y + 4, x, y + 12);
    ctx.quadraticCurveTo(x - 9, y + 4, x - 9, y - 7);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "#0A2540"; ctx.lineWidth = 1.5; ctx.stroke();
    ctx.strokeStyle = "#ffffff"; ctx.lineWidth = 2;
    ctx.beginPath(); ctx.moveTo(x - 4, y); ctx.lineTo(x - 1, y + 4); ctx.lineTo(x + 5, y - 4); ctx.stroke();
  } else {
    ctx.strokeStyle = "#F87171";
    ctx.lineWidth = 6;
    ctx.beginPath(); ctx.arc(x, y - 2, 8, Math.PI, 0); ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(x - 8, y - 2); ctx.lineTo(x - 8, y + 7);
    ctx.moveTo(x + 8, y - 2); ctx.lineTo(x + 8, y + 7);
    ctx.stroke();
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(x - 11, y + 4, 6, 5);
    ctx.fillRect(x + 5, y + 4, 6, 5);
    ctx.strokeStyle = "#0A2540"; ctx.lineWidth = 1.2;
    ctx.strokeRect(x - 11, y + 4, 6, 5);
    ctx.strokeRect(x + 5, y + 4, 6, 5);
  }
  ctx.restore();
}

function drawGameOver(ctx, s) {
  ctx.fillStyle = "rgba(10, 37, 64, 0.78)"; ctx.fillRect(0, 0, s.W, s.H);
  ctx.fillStyle = "#FFD700"; ctx.textAlign = "center"; ctx.textBaseline = "middle";
  const titleSize = Math.min(56, Math.floor(s.W * 0.14));
  ctx.font = `bold ${titleSize}px Anton, sans-serif`;
  ctx.fillText(s.timedOut ? "TIME'S UP!" : "WIPEOUT!", s.W / 2, s.H / 2 - 24);
  ctx.fillStyle = "#fff"; ctx.font = `${Math.min(22, Math.floor(s.W * 0.055))}px Outfit, sans-serif`;
  ctx.fillText(`You collected ${s.coinCount} $JETDOG`, s.W / 2, s.H / 2 + 24);
}

export default GameCanvas;
;
